import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { examService } from '../../services/examService';
import { academicService } from '../../services/academicService';
import { useAuth } from '../../context/AuthContext';
import { Award, Users, BookOpen, Download } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const StudentResultsPage = () => {
  const { user } = useAuth();
  const [results, setResults] = React.useState([]);
  const [exams, setExams] = React.useState([]);
  const [selectedExam, setSelectedExam] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [filterClass, setFilterClass] = React.useState('');
  const [filterSubject, setFilterSubject] = React.useState('');

  const fetchExams = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch exams for classes assigned to this staff
      const res = await examService.getExams({ teacherId: user?.id });
      const examsData = res.data?.data?.exams || res.data?.data || [];
      setExams(examsData);
      
      // If there are exams, fetch results for the first one
      if (examsData.length > 0 && !selectedExam) {
        setSelectedExam(examsData[0].id);
        await fetchResults(examsData[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load exams');
      console.error('Error fetching exams:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (examId) => {
    try {
      setLoading(true);
      const res = await examService.getExamResults(examId);
      const resultsData = res.data?.data?.results || res.data?.data?.data?.results || [];
      setResults(resultsData);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load exam results');
      console.error('Error fetching results:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (user?.id) {
      fetchExams();
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (selectedExam) {
      fetchResults(selectedExam);
    }
  }, [selectedExam]);

  const columns = [
    { 
      id: 'student', 
      header: 'Student', 
      accessorKey: 'student',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        const student = row.original.student;
        if (!student) return 'N/A';
        return `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || 'N/A';
      },
      sortable: true
    },
    { 
      id: 'score', 
      header: 'Score', 
      accessorKey: 'marksObtained',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        const marks = row.original.marksObtained || 0;
        const total = row.original.exam?.totalMarks || row.original.totalMarks || 100;
        return `${marks}/${total}`;
      },
      sortable: true
    },
    { 
      id: 'percentage', 
      header: 'Percentage', 
      accessorKey: 'percentage',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        const percentage = row.original.percentage || row.original.percentageScore || 0;
        return `${percentage.toFixed(1)}%`;
      },
      sortable: true
    },
    { 
      id: 'grade', 
      header: 'Grade', 
      accessorKey: 'grade',
      cell: ({ row }) => {
        if (!row?.original) return <Badge className="bg-gray-100 text-gray-800">N/A</Badge>;
        const grade = row.original.grade;
        const gradeColors = {
          'A': 'bg-green-100 text-green-800',
          'B': 'bg-blue-100 text-blue-800',
          'C': 'bg-yellow-100 text-yellow-800',
          'D': 'bg-orange-100 text-orange-800',
          'F': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={gradeColors[grade] || 'bg-gray-100 text-gray-800'}>
            {grade || 'N/A'}
          </Badge>
        );
      },
      sortable: true
    },
    { 
      id: 'status', 
      header: 'Status', 
      accessorKey: 'status',
      cell: ({ row }) => {
        if (!row?.original) return <Badge className="bg-gray-100 text-gray-800">N/A</Badge>;
        const status = row.original.status || 'pending';
        const statusColors = {
          'graded': 'bg-green-100 text-green-800',
          'pending': 'bg-yellow-100 text-yellow-800',
          'submitted': 'bg-blue-100 text-blue-800'
        };
        return (
          <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
            {status}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        if (!row?.original) return null;
        return (
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => window.location.assign(`/dashboard/exams/${row.original.examId}/results/${row.original.id}`)}
          >
            View Details
          </Button>
        );
      }
    }
  ];

  if (loading && !results.length) return <LoadingSpinner />;
  if (error && !results.length) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchExams}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Student Exam Results</h2>
          <p className="text-gray-600">View exam results for students in your assigned classes</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Exams</p>
                <p className="text-2xl font-bold text-gray-900">{exams.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Results</p>
                <p className="text-2xl font-bold text-gray-900">{results.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Graded</p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.filter(r => r.status === 'graded').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exam Selection */}
      {exams.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Exam</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exams.map((exam) => (
                <Card 
                  key={exam.id}
                  className={`cursor-pointer transition-all ${
                    selectedExam === exam.id ? 'ring-2 ring-indigo-500' : ''
                  }`}
                  onClick={() => setSelectedExam(exam.id)}
                >
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">{exam.title}</h3>
                    <p className="text-sm text-gray-600">{exam.subjectName || exam.subject?.name}</p>
                    <p className="text-sm text-gray-600">{exam.className || exam.class?.name}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {exam.examDate ? new Date(exam.examDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      {selectedExam ? (
        results.length === 0 ? (
          <EmptyState 
            icon={Award}
            title="No results found"
            description="No exam results available for the selected exam."
          />
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Exam Results</CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable data={results} columns={columns} searchable />
            </CardContent>
          </Card>
        )
      ) : (
        <EmptyState 
          icon={Award}
          title="No exams available"
          description="No exams found for your assigned classes."
        />
      )}
    </div>
  );
};

export default StudentResultsPage;

